import { formatDate, formatTime } from '@/utils/date'

export type CustomerAttachment = {
  id: string
  name: string
  size: number
  state: 'Available' | 'Processing' | 'Unavailable'
}

export type TicketMessage = {
  id: string
  authorName: string
  html: string
  createdAtUtc: string
  attachments: CustomerAttachment[]
}

export type Ticket = {
  id: number
  subject: string
  status: string
  statusGroup?: CustomerSupportStatus
  hasUnreadReply?: boolean
  canCustomerReply?: boolean
  updatedAtUtc: string
  orderId: number | null
  productId: number | null
  productName: string | null
  awaitingTeam?: boolean | null
  latestExcerpt?: string | null
  channel?: string | null
  resolvedAtUtc?: string | null
  chatActive?: boolean
  messages?: TicketMessage[]
}

export function isLiveChatTicket(ticket: Pick<Ticket, 'channel' | 'subject'>) {
  if (ticket.channel === 'CHAT') return true
  if (ticket.channel) return false
  return ticket.subject === 'Website conversation'
}

export const websiteChatStatus = 'Website chat'

/** Reply eligibility is decided by the API; chat continues in its own channel. */
export function replyWindowClosed(
  ticket: Pick<
    Ticket,
    'channel' | 'subject' | 'statusGroup' | 'status' | 'canCustomerReply'
  >
) {
  if (isLiveChatTicket(ticket)) return true
  if (typeof ticket.canCustomerReply === 'boolean')
    return !ticket.canCustomerReply
  return ticket.statusGroup === 'Closed' || ticket.status === 'Closed'
}

export type CustomerSupportStatus =
  | 'NeedsReply'
  | 'WithSupport'
  | 'Resolved'
  | 'Closed'

export type SupportTone = 'working' | 'yours' | 'replied' | 'settled'

export type SupportPresence = {
  tone: SupportTone
  label: string
  title: string
  body: string
  listNote: string | null
  settled: boolean
}

function plainStatus(status: string) {
  return status.replaceAll('\u2019', "'").replaceAll('\u2018', "'")
}

export function supportPresence(ticket: {
  status: string
  awaitingTeam?: boolean | null
  messages?: { authorName: string }[] | null
}): SupportPresence {
  const status = plainStatus(ticket.status)
  const settled = status === 'Resolved' || status === 'Closed'
  const looking = status === "We're looking into it"
  const waiting = status === 'Waiting for your reply'
  const messages = ticket.messages
  const hasMessages = Array.isArray(messages)
  const last = hasMessages && messages.length > 0 ? messages.at(-1) : null
  const teamHasSpoken = hasMessages
    ? messages.some(message => message.authorName === 'Vanishing Inc.')
    : null
  const teamSpokeLast = last?.authorName === 'Vanishing Inc.'
  const awaiting = hasMessages
    ? !settled && !waiting && !teamSpokeLast
    : typeof ticket.awaitingTeam === 'boolean'
      ? ticket.awaitingTeam
      : null

  if (settled)
    return {
      tone: 'settled',
      label: status,
      title: 'This conversation is settled',
      body: 'Reply below if something else comes up, and we’ll pick it back up.',
      listNote: null,
      settled: true,
    }
  if (waiting)
    return {
      tone: 'yours',
      label: 'Your turn',
      title: 'We need a little more from you',
      body: 'Our reply is above. Add the detail we asked for, or anything else that would help, and we’ll take it from there.',
      listNote: 'We replied. Have a look when you can.',
      settled: false,
    }
  if (looking)
    return {
      tone: 'working',
      label: 'Looking into it',
      title: 'We’re looking into this',
      body: 'This one needs a closer look. We’ll update you in this conversation. Anything that helps can go below.',
      listNote: 'We’re looking into this. We’ll update you here.',
      settled: false,
    }
  if (hasMessages && teamHasSpoken && !teamSpokeLast)
    return {
      tone: 'working',
      label: 'With our team',
      title: 'We’ve got your latest note',
      body: 'Our team is reading it and will reply here. You can still add a photo or anything we should know.',
      listNote: 'We’ve got your latest note.',
      settled: false,
    }
  if (awaiting === true || (hasMessages && teamHasSpoken === false))
    return {
      tone: 'working',
      label: 'With our team',
      title: 'We’re working on this',
      body: 'There’s no reply yet. Your message is with the Vanishing Inc. team. We’ll reply in this conversation. If a photo, an order number, or something you forgot would help, add it below.',
      listNote: 'No reply yet. We’re working on this.',
      settled: false,
    }
  if (awaiting === false)
    return {
      tone: 'replied',
      label: 'We replied',
      title: 'We’ve replied',
      body: 'Have a look above. A photo, an order number, or anything you forgot can go below, and it stays with this conversation.',
      listNote: 'We replied. Open this if you want to add anything.',
      settled: false,
    }
  return {
    tone: 'working',
    label: supportStatusLabel(status),
    title: 'With our team',
    body: 'This conversation is open with our team. Add anything we should know below.',
    listNote: null,
    settled: false,
  }
}

export const supportField =
  'mt-1 w-full rounded-lg border border-gray-300 bg-white p-3 text-base focus:border-vi-red focus:ring-0 focus:outline-2 focus:outline-vi-red'
export const supportButton =
  'rounded-lg bg-gray-900 px-4 py-3 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-50'

export function escapeHtml(text: string) {
  const entities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }
  return text.replace(/[&<>"']/g, character => entities[character])
}

export function supportStatusLabel(status: string) {
  if (status === 'NeedsReply') return 'Needs your reply'
  if (status === 'WithSupport') return "We're working on it"
  if (status === 'OnHold') return 'On hold'
  return status
}

export function supportTimestamp(value: string) {
  return `${formatDate(value)}, ${formatTime(value)}`
}

export function fileSize(bytes: number) {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  if (bytes === 0) return '0 bytes'
  return `${Math.ceil(bytes / 1000)} KB`
}
